import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HOME_HERO_PORTRAIT_MEDIA, HOME_HERO_STAGE_MIN_CLASS } from "../constants/homeHeroStage";
import { useMediaQuery } from "../utils/useMediaQuery";
import { useTranslation } from "../i18n/useTranslation";

const HERO_FADE_DURATION_MS = 4200;
const HERO_INITIAL_DELAY_MS = 60;
const HERO_HEADER_REVEAL_MS = 1000;
const HERO_DID_YOU_KNOW_AUTO_CLOSE_MS = 4000;

const MAGIC_VIDEO_URL =
  "https://pub-292a7cb35002422fa1ebf3f3e84ea836.r2.dev/MySipur.mp4";

/** Sparkles scoped to hero only; primary grid spans full hero height plus an extra strip near the fold. */
const HERO_SPARKLE_COLS = 14;
/** Full-density counts; subsampling preserves even coverage (edges + bottom band). */
const HERO_SPARKLE_PRIMARY_FULL = 110;
const HERO_SPARKLE_BOTTOM_BAND_FULL = 32;
/** Thinned vs full grid (prior step ×60% stars = −40% from previous). */
const HERO_SPARKLE_KEEP_RATIO = 0.6 * 0.8 * 0.7 * 0.6;
const HERO_SPARKLE_PRIMARY = Math.round(HERO_SPARKLE_PRIMARY_FULL * HERO_SPARKLE_KEEP_RATIO);
const HERO_SPARKLE_BOTTOM_BAND = Math.round(HERO_SPARKLE_BOTTOM_BAND_FULL * HERO_SPARKLE_KEEP_RATIO);

/** Unique sorted indices spanning [0, total-1] for even spatial thinning. */
function evenlySpacedUniqueIndices(total: number, keep: number): number[] {
  if (keep >= total) return Array.from({ length: total }, (_, i) => i);
  const out = new Set<number>();
  let prev = -1;
  for (let k = 0; k < keep; k += 1) {
    const target = Math.round((k * (total - 1)) / Math.max(1, keep - 1));
    let idx = Math.min(total - 1, Math.max(0, target));
    while (idx <= prev && idx < total - 1) idx += 1;
    prev = idx;
    out.add(idx);
  }
  while (out.size < keep) {
    let filler = total - 1;
    while (filler >= 0 && out.has(filler)) filler -= 1;
    if (filler < 0) break;
    out.add(filler);
  }
  return Array.from(out).sort((a, b) => a - b);
}

const HOME_HERO_MAGIC_SPARKLE_STYLES = (() => {
  type SparkleStyle = {
    top: string;
    left: string;
    size: number;
    delay: string;
    duration: string;
  };

  const rowCount = Math.ceil(HERO_SPARKLE_PRIMARY_FULL / HERO_SPARKLE_COLS);
  const denom = Math.max(1, rowCount - 1);

  const primaryFull: SparkleStyle[] = [];
  for (let index = 0; index < HERO_SPARKLE_PRIMARY_FULL; index += 1) {
    const row = Math.floor(index / HERO_SPARKLE_COLS);
    const col = index % HERO_SPARKLE_COLS;
    const topPct = 2 + (row / denom) * 93 + (col % 3) * 0.4;
    const leftRaw = 3.5 + col * 6.85 + (row % 3) * 1.05;
    const size = 6 + (index % 7);
    primaryFull.push({
      top: `${Math.min(topPct, 97.5)}%`,
      left: `${Math.min(leftRaw, 94)}%`,
      size,
      delay: `${(index * 53) % 1700}ms`,
      duration: `${1180 + (index % 7) * 220}ms`,
    });
  }

  const bottomFull: SparkleStyle[] = [];
  for (let b = 0; b < HERO_SPARKLE_BOTTOM_BAND_FULL; b += 1) {
    const i = HERO_SPARKLE_PRIMARY_FULL + b;
    const topPct = 80 + ((b % 8) / 8) * 17;
    const leftRaw = 4 + (((b * 31) % 89) / 89) * 87;
    const size = 5 + ((b + 4) % 7);
    bottomFull.push({
      top: `${Math.min(topPct, 97)}%`,
      left: `${Math.min(leftRaw, 93)}%`,
      size,
      delay: `${(i * 47) % 1700}ms`,
      duration: `${1120 + (b % 7) * 210}ms`,
    });
  }

  const primaryPick = evenlySpacedUniqueIndices(HERO_SPARKLE_PRIMARY_FULL, HERO_SPARKLE_PRIMARY);
  const bottomPick = evenlySpacedUniqueIndices(HERO_SPARKLE_BOTTOM_BAND_FULL, HERO_SPARKLE_BOTTOM_BAND);

  const positions = [
    ...primaryPick.map((i) => primaryFull[i]),
    ...bottomPick.map((i) => bottomFull[i]),
  ];

  return positions;
})();

/** Skip sparkles that would sit on the hero illustration’s face / portrait (bg is left on LTR, right on RTL). */
function isSparkleOverHeroPortrait(leftPct: number, topPct: number, rtl: boolean): boolean {
  const l = rtl ? 100 - leftPct : leftPct;
  const inPortraitBand = l >= 3 && l <= 46 && topPct >= 16 && topPct <= 76;
  return inPortraitBand;
}

/** Portrait hero art grounds the family in the lower band — keep sparkles out of that area. */
function isSparkleOverPortraitHeroFamily(leftPct: number, topPct: number): boolean {
  return topPct >= 54 && leftPct >= 8 && leftPct <= 92;
}

/** Skip sparkles stacked in the upper-trailing corner (logo / header-adjacent area). */
function isSparkleInHeroTopRightCorner(leftPct: number, topPct: number): boolean {
  return topPct <= 42 && leftPct >= 58;
}

type HomeHeroSparkleStyle = {
  top: string;
  left: string;
  size: number;
  delay: string;
  duration: string;
};

/** Request fullscreen where supported (used with orientation lock on small viewports). */
function requestFullscreenForElement(el: Element): Promise<void> {
  const h = el as HTMLElement & {
    webkitRequestFullscreen?: () => void;
    mozRequestFullScreen?: () => void;
    msRequestFullscreen?: () => void;
  };
  if (typeof document === "undefined") return Promise.resolve();
  if (typeof el.requestFullscreen === "function") {
    return el.requestFullscreen({ navigationUI: "hide" } as Parameters<Element["requestFullscreen"]>[0]).catch(() =>
      /** Some engines ignore Options */ el.requestFullscreen!(),
    );
  }
  if (typeof h.webkitRequestFullscreen === "function") {
    h.webkitRequestFullscreen();
    return Promise.resolve();
  }
  if (typeof h.msRequestFullscreen === "function") {
    h.msRequestFullscreen();
    return Promise.resolve();
  }
  return Promise.reject(new Error("fullscreen"));
}

/** Exit fullscreen (standard + legacy WebKit). */
function exitFullscreenDocument(): Promise<void> {
  const d = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => void;
  };
  if (document.fullscreenElement != null && typeof document.exitFullscreen === "function") {
    return document.exitFullscreen().catch(() => {});
  }
  if (d.webkitFullscreenElement != null && typeof d.webkitExitFullscreen === "function") {
    d.webkitExitFullscreen();
  }
  return Promise.resolve();
}

/** Deliberately dense cluster in geometric top-left — %top kept under the portrait-band floor so extras never sit on the face. */
const HOME_HERO_TOP_LEFT_EXTRA_SPARKLES: readonly HomeHeroSparkleStyle[] = [
  { top: "3.8%", left: "7%", size: 8, delay: "100ms", duration: "1180ms" },
  { top: "5.2%", left: "17%", size: 6, delay: "460ms", duration: "1460ms" },
  { top: "6.8%", left: "5%", size: 7, delay: "240ms", duration: "1290ms" },
  { top: "7.8%", left: "28%", size: 9, delay: "0ms", duration: "1370ms" },
  { top: "4.5%", left: "37%", size: 7, delay: "820ms", duration: "1310ms" },
  { top: "10%", left: "12%", size: 6, delay: "640ms", duration: "1520ms" },
  { top: "11.2%", left: "43%", size: 8, delay: "380ms", duration: "1240ms" },
  { top: "9.2%", left: "22%", size: 7, delay: "920ms", duration: "1400ms" },
  { top: "2.5%", left: "14%", size: 6, delay: "510ms", duration: "1430ms" },
  { top: "5.9%", left: "31%", size: 7, delay: "300ms", duration: "1340ms" },
  { top: "7%", left: "39%", size: 6, delay: "700ms", duration: "1260ms" },
];

const HeroSection: React.FC = () => {
  const usePortraitHero = useMediaQuery(HOME_HERO_PORTRAIT_MEDIA);
  const { t, uiLocale } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [didYouKnowOpen, setDidYouKnowOpen] = useState(false);
  const [magicVideoVisible, setMagicVideoVisible] = useState(false);
  const didYouKnowTriggerRef = useRef<HTMLButtonElement>(null);
  const magicVideoRef = useRef<HTMLVideoElement>(null);
  const magicVideoTriggerRef = useRef<HTMLButtonElement>(null);
  const cineBackdropRef = useRef<HTMLDivElement | null>(null);
  const cineWrapRef = useRef<HTMLDivElement | null>(null);
  const cineInnerRef = useRef<HTMLDivElement | null>(null);
  const cineTitleRef = useRef<HTMLDivElement | null>(null);
  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Invalidate in-flight fullscreen + orientation work when overlay closes quickly. */
  const magicVideoLandscapeLeaseRef = useRef(0);
  const didYouKnowTitleId = `hero-did-you-know-${useId().replace(/:/g, "")}`;

  const releaseMagicVideoPhoneLandscape = useCallback(() => {
    magicVideoLandscapeLeaseRef.current += 1;
    try {
      window.screen.orientation?.unlock?.();
    } catch {
      /* ignore — not supported or sandboxed */
    }
    void exitFullscreenDocument();
  }, []);

  const closeDidYouKnow = useCallback(() => {
    setDidYouKnowOpen(false);
    requestAnimationFrame(() => didYouKnowTriggerRef.current?.focus());
  }, []);

  const closeMagicVideo = useCallback(() => {
    releaseMagicVideoPhoneLandscape();
    cineInnerRef.current?.classList.remove("open");
    cineTitleRef.current?.classList.remove("hidden");
    if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    const el = magicVideoRef.current;
    if (el) { el.pause(); el.currentTime = 0; }
    setTimeout(() => {
      cineBackdropRef.current?.classList.remove("active");
      cineWrapRef.current?.classList.remove("active");
      setMagicVideoVisible(false);
      window.dispatchEvent(new CustomEvent("mysipur:hero-magic-video-close"));
      requestAnimationFrame(() => magicVideoTriggerRef.current?.focus());
    }, 500);
  }, [releaseMagicVideoPhoneLandscape]);

  useEffect(() => {
    const timer = window.setTimeout(() => setIsVisible(true), HERO_INITIAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const doneTimer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("mysipur:hero-fade-complete"));
    }, HERO_INITIAL_DELAY_MS + HERO_HEADER_REVEAL_MS);

    return () => window.clearTimeout(doneTimer);
  }, []);

  useEffect(() => {
    if (!didYouKnowOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeDidYouKnow();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    const autoCloseTimer = window.setTimeout(
      () => closeDidYouKnow(),
      HERO_DID_YOU_KNOW_AUTO_CLOSE_MS
    );
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(autoCloseTimer);
    };
  }, [didYouKnowOpen, closeDidYouKnow]);

  useEffect(() => {
    if (!magicVideoVisible) {
      document.body.style.overflow = "";
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault(); closeMagicVideo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [magicVideoVisible, closeMagicVideo]);

  const toggleMagicVideo = useCallback(() => {
    if (magicVideoVisible) { closeMagicVideo(); return; }

    setMagicVideoVisible(true);
    window.dispatchEvent(new CustomEvent("mysipur:hero-magic-video-open"));
    document.body.style.overflow = "hidden";
    cineBackdropRef.current?.classList.add("active");
    cineWrapRef.current?.classList.add("active");

    if (usePortraitHero) {
      const lease = ++magicVideoLandscapeLeaseRef.current;
      const inner = cineInnerRef.current;
      void (async () => {
        if (inner) await requestFullscreenForElement(inner).catch(() => {});
        if (magicVideoLandscapeLeaseRef.current !== lease) {
          await exitFullscreenDocument();
          return;
        }
        try {
          await window.screen.orientation?.lock?.("landscape");
        } catch {
          /* often requires fullscreen or is unsupported (e.g. some iOS Safari) */
        }
      })();
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        cineInnerRef.current?.classList.add("open");
        setTimeout(() => {
          void magicVideoRef.current?.play().catch(() => {});
          titleTimerRef.current = setTimeout(() => {
            cineTitleRef.current?.classList.add("hidden");
          }, 3000);
        }, 400);
      });
    });
  }, [magicVideoVisible, closeMagicVideo, usePortraitHero]);

  const openMagicVideoFromHeader = useCallback(() => {
    if (!magicVideoVisible) {
      toggleMagicVideo();
    }
  }, [magicVideoVisible, toggleMagicVideo]);

  useEffect(() => {
    const onHeader = () => openMagicVideoFromHeader();
    window.addEventListener("mysipur:open-hero-magic", onHeader);
    return () => window.removeEventListener("mysipur:open-hero-magic", onHeader);
  }, [openMagicVideoFromHeader]);

  useEffect(() => {
    return () => {
      releaseMagicVideoPhoneLandscape();
    };
  }, [releaseMagicVideoPhoneLandscape]);

  useEffect(() => {
    const st = (location.state as { openHeroMagic?: boolean } | null)?.openHeroMagic;
    if (!st) return;
    openMagicVideoFromHeader();
    navigate(".", { replace: true, state: {} });
  }, [location.state, navigate, openMagicVideoFromHeader]);

  return (
    <section
      className={`relative flex ${HOME_HERO_STAGE_MIN_CLASS} w-full min-w-0 flex-col items-center px-4 pt-[3.85rem] max-sm:pb-[max(0.5rem,calc(0.35rem+env(safe-area-inset-bottom,0px)))] text-center min-[390px]:px-5 sm:px-6 sm:pt-[5.25rem] sm:pb-16 md:pb-20`}
    >
      <div className="magic-sparkle-layer pointer-events-none absolute inset-0 z-[1]" aria-hidden="true">
        {(
          [
            ...HOME_HERO_MAGIC_SPARKLE_STYLES.filter((sparkle) => {
              const topPct = Number.parseFloat(sparkle.top);
              const leftPct = Number.parseFloat(sparkle.left);
              if (Number.isNaN(topPct) || Number.isNaN(leftPct)) return true;
              const rtl = uiLocale === "he";
              if (usePortraitHero) {
                if (isSparkleOverPortraitHeroFamily(leftPct, topPct)) return false;
              } else if (isSparkleOverHeroPortrait(leftPct, topPct, rtl)) return false;
              if (isSparkleInHeroTopRightCorner(leftPct, topPct)) return false;
              return true;
            }),
            ...HOME_HERO_TOP_LEFT_EXTRA_SPARKLES,
          ] as HomeHeroSparkleStyle[]
        ).map((sparkle, index) => (
          <span
            key={`hero-sp-${index}`}
            className="magic-sparkle"
            style={{
              top: sparkle.top,
              left: sparkle.left,
              width: `${sparkle.size}px`,
              height: `${sparkle.size}px`,
              animationDelay: sparkle.delay,
              animationDuration: sparkle.duration,
            }}
          />
        ))}
      </div>
      <div
        className={`relative z-10 flex w-full min-w-0 max-w-lg flex-col items-center text-center transition-all duration-[${HERO_FADE_DURATION_MS}ms] ease-[cubic-bezier(0.22,1,0.36,1)] max-sm:min-h-0 max-sm:flex-1 max-sm:pb-0 sm:max-w-xl lg:max-w-2xl ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
        }`}
      >
        {uiLocale === "en" && (
          <div className="relative z-20 mb-4 w-full self-start text-left sm:absolute sm:inset-x-auto sm:mb-0 sm:left-4 sm:right-auto sm:top-2">
            <button
              ref={didYouKnowTriggerRef}
              type="button"
              className="text-sm font-medium text-white/90 hover:text-white underline underline-offset-2 drop-shadow focus:outline-none focus:ring-2 focus:ring-white/80 focus:ring-offset-2 focus:ring-offset-transparent rounded-sm"
              onClick={() => setDidYouKnowOpen(true)}
              aria-expanded={didYouKnowOpen}
              aria-controls="hero-did-you-know-popover"
            >
              {t("home.heroDidYouKnowLink")}
            </button>
            {didYouKnowOpen && (
              <div
                id="hero-did-you-know-popover"
                role="dialog"
                aria-modal="true"
                aria-labelledby={didYouKnowTitleId}
                className="absolute left-0 top-full z-30 mt-2 max-h-[70dvh] w-max max-w-[min(19rem,calc(100vw-2rem))] overflow-y-auto rounded-xl border border-stone-200/90 bg-white px-4 py-3 shadow-lg"
              >
                <p id={didYouKnowTitleId} className="text-sm text-body leading-snug">
                  {t("home.heroDidYouKnowBubble")}
                </p>
              </div>
            )}
          </div>
        )}
        {/* Logo */}
        <img
          src="https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/home/logo.png"
          alt="MySipur"
          className="mb-3 h-20 max-w-[min(100%,220px)] object-contain object-center drop-shadow-lg brightness-125 contrast-105 max-sm:mb-2 sm:mb-4 sm:h-28 md:max-w-none"
        />

        <div
          className="mb-6 hidden items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-md sm:inline-flex"
        >
          <span
            className="h-1.5 w-1.5 rounded-full bg-brandBlue"
            aria-hidden
          />
          {t("home.heroEyebrow")}
        </div>

        <h1
          className={`mb-0 w-full max-w-full text-pretty break-words px-0 font-bold leading-[1.06] tracking-[-0.02em] text-white [overflow-wrap:anywhere] max-sm:mt-2 max-sm:rounded-[1.375rem] max-sm:border max-sm:border-white/45 max-sm:bg-white/[0.24] max-sm:px-5 max-sm:py-5 max-sm:text-heading max-sm:backdrop-blur-xl max-sm:shadow-[inset_0_1px_rgba(255,255,255,0.5),0_8px_32px_rgba(64,89,107,0.1)] max-sm:[text-shadow:none] sm:mb-10 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:text-white sm:leading-[1.08] sm:backdrop-blur-none sm:shadow-none sm:[text-shadow:0_2px_4px_rgba(0,0,0,0.40)] ${
            uiLocale === "he" ? "font-sans" : "font-display"
          }`}
          style={{
            fontSize: usePortraitHero
              ? "clamp(1.75rem, 6.2vw + 0.95rem, 5.5rem)"
              : "clamp(1.875rem, 4vw + 0.75rem, 5.5rem)",
          }}
        >
          {uiLocale === "en" ? (
            <>
              Create a{" "}
              <em className="font-display italic text-brandOrange">Magical</em>{" "}
              Storybook About Your Family
            </>
          ) : (
            <>
              צרו ספר ילדים{" "}
              <span className="text-brandOrange">קסום</span> על המשפחה שלכם
            </>
          )}
        </h1>

        <div className="mx-auto mt-6 flex w-full max-w-lg flex-col items-stretch gap-3 max-sm:mt-auto max-sm:w-full max-sm:translate-y-[1.125rem] max-sm:pt-0 sm:static sm:mt-0 sm:translate-y-0 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
          <Link
            to="/create/language"
            className="hidden min-h-[3.25rem] items-center justify-center rounded-2xl bg-brandOrange px-5 py-3 text-[0.95rem] font-semibold leading-tight text-white shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-500 hover:shadow-glow focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 sm:inline-flex sm:h-14 sm:px-8 sm:text-base"
          >
            {t("home.startYourBook")}
          </Link>
          <button
            ref={magicVideoTriggerRef}
            type="button"
            className="inline-flex min-h-[3.25rem] w-full items-center justify-center rounded-2xl bg-brandOrange px-5 py-3 text-[0.95rem] font-semibold leading-tight text-white shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-500 hover:shadow-glow focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 max-sm:shadow-[0_-10px_28px_rgba(0,0,0,0.2)] sm:h-14 sm:w-auto sm:px-8 sm:text-base sm:shadow-card"
            aria-expanded={magicVideoVisible}
            onClick={toggleMagicVideo}
          >
            {t("home.seeTheMagic")}
          </button>
        </div>
      </div>
      {/* Cinematic video - always rendered so close animation plays */}
      <div
        ref={cineBackdropRef}
        role="button"
        tabIndex={magicVideoVisible ? 0 : -1}
        aria-hidden={!magicVideoVisible}
        aria-label={t("home.closeVideo")}
        className="cine-backdrop"
        onClick={closeMagicVideo}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            closeMagicVideo();
          }
        }}
      />
      <div ref={cineWrapRef} className="cine-wrap">
        <div ref={cineInnerRef} className="cine-inner">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- marketing clip; add VTT when provided */}
          <video
            ref={magicVideoRef}
            src={MAGIC_VIDEO_URL}
            playsInline
            preload="metadata"
            aria-label={t("home.seeTheMagicVideoAriaLabel")}
            onEnded={closeMagicVideo}
          />
          <div className="cine-vignette" />
          <button
            type="button"
            aria-label={t("home.playMagicVideo")}
            className="absolute inset-0 z-[5] cursor-pointer border-0 bg-transparent p-0"
            onClick={() => {
              const v = magicVideoRef.current;
              if (!v) return;
              v.paused ? void v.play().catch(() => {}) : v.pause();
            }}
          />
          <div ref={cineTitleRef} className="cine-title-overlay">
            <h3>{t("home.seeTheMagic")}</h3>
            <p>{t("home.seeTheMagicVideoAriaLabel")}</p>
          </div>
          <button
            className="cine-close"
            onClick={closeMagicVideo}
            aria-label={t("home.closeVideo")}
          >✕</button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
